import type { ClassId, Faction } from '../core/constants.ts';
import type { ChatMessage, ChatScope } from '../core/chat.ts';
import type { LobbySummary } from '../core/lobby.ts';
import type { PlayerInput, SimEvent, SimState } from '../core/sim.ts';

/**
 * What travels between a client and a room.
 *
 * Named in one file that both ends import, because a protocol written twice is a
 * protocol that disagrees with itself — and the disagreement shows up as a
 * player whose spells do not fire, three weeks later, on somebody else's
 * machine.
 *
 * Deliberately small. Everything the game already had was built so that a client
 * sends intentions and receives a picture; there is nothing here that is not one
 * of those two things, plus the handful of messages needed to find a room and
 * sit down in it.
 */

// ── Client to server ─────────────────────────────────────────────────────────

export type ClientMessage =
  /**
   * Opening a room on the server, rather than in your own tab.
   *
   * Its absence was the whole of a bug: "create lobby" built a `LobbySettings`,
   * never sent it anywhere, and ran the match locally — so the room existed only
   * in the host's browser and no amount of refreshing on a second machine could
   * list a thing that was never anywhere else. The two persistent rooms were the
   * only ones a server ever had.
   *
   * The server answers `opened` with the id it actually assigned, which the
   * client then `join`s exactly as it would one it found in the list. Creating
   * and sitting down stay two messages: a host picks a faction on the lobby
   * screen *after* the room exists, and folding them together would mean either
   * asking before there is a room to ask about or inventing an answer.
   */
  | {
    t: 'create';
    /** The same door as `join`. A room nobody may enter is not worth opening. */
    secret?: string;
    name: string;
    mapId?: string;
    /** The whole rotation, in order. `mapId` is just the first of it. */
    mapPool?: string[];
    carryXp?: boolean;
    maxPlayers?: number;
    xpScale?: number;
    roundSeconds?: number;
    /** Rounds to play on a map: 1, 3 or 5. */
    bestOf?: number;
    /**
     * Bots to seat on each side when the room opens.
     *
     * They have to be asked for over the wire, because a hosted room is not this
     * process: the client's own bot-seeding runs against its local room, which a
     * hosted match never uses. Without this a lobby opened with four bots a side
     * arrived on the server empty, and a host stood alone in a match that could
     * not start.
     */
    bots?: { order?: number; balance?: number; chaos?: number };
  }
  /**
   * Sitting down. The server answers with `welcome` or `denied`.
   *
   * `bots` is honoured only for the first person into an empty room — the host,
   * arriving from the lobby screen where they chose them. It cannot ride on
   * `create` because a lobby is opened the moment its screen does, minutes
   * before anybody has decided how many bots they want.
   *
   * A client asks for a side and a discipline and does not get to insist on
   * either: the room may put them somewhere else to keep the sides level, and
   * the answer says where they actually landed.
   */
  | {
    t: 'join';
    /** Bots to seat, honoured only for the first arrival. See above. */
    bots?: { order?: number; balance?: number; chaos?: number };
    room: string;
    name: string;
    /**
     * The shared secret, when the server is asking for one.
     *
     * A door rather than a vault: it turns "anybody who finds the URL" into
     * "anybody you told", which for a game being tested among friends is exactly
     * the distinction that matters. A server with none set asks for none, so
     * local development is unchanged.
     */
    secret?: string;
    faction: Faction;
    cls: ClassId;
    loadout?: string[];
  }
  /**
   * A frame's intentions.
   *
   * Sent every frame and consumed on the next tick, so a client running at two
   * hundred hertz against a sixty-hertz room simply has most of them replaced
   * before they are read. That is correct and is why nothing is queued.
   */
  | {
    t: 'input';
    input: PlayerInput;
    /**
     * Which input this is, counting up from the client's first.
     *
     * The whole of what makes prediction possible. A client runs ahead on its
     * own inputs and needs to know which of them the room has seen, so it can
     * replay only the rest on top of the answer. Without it the client would
     * have to throw away everything it had predicted every time a snapshot
     * arrived, which is the same as not predicting at all.
     */
    seq: number;
  }
  | { t: 'chat'; scope: ChatScope; text: string }
  /** Saying you are ready to start. Only answered while the room is in its lobby. */
  | { t: 'ready'; on: boolean }
  /**
   * Adding a bot to a side, in the lobby only.
   *
   * Has to be a message because the room is joined *before* the lobby screen
   * opens — so the bots a host picks in the lobby cannot ride along on the join
   * the way the ones they picked earlier do. Without it the button was there and
   * did nothing, which is worse than not having it.
   */
  | { t: 'addBot'; faction: Faction }
  /**
   * Changing what you are called, in the lobby only.
   *
   * The lobby is the one place a name is asked for and the room is joined before
   * it opens, so without this a name typed there reached a local variable and
   * nobody else's screen.
   */
  | { t: 'rename'; name: string }
  /** Changing side or discipline, in the lobby only. */
  | { t: 'reseat'; faction: Faction; cls: ClassId; loadout?: string[] }
  | { t: 'leave' }
  /** Kept alive so a silent connection can be told apart from a dead one. */
  | { t: 'ping' };

// ── Server to client ─────────────────────────────────────────────────────────

export type ServerMessage =
  /**
   * You are in, you are this player, and here is the world.
   *
   * The arena travels once. It is the largest thing in a `SimState` by far —
   * every box, torch and named area of a cathedral — and almost none of it
   * changes: what a pool's owner is and whether a shrine still stands, and both
   * ride along in the snapshots instead.
   */
  | {
    t: 'welcome';
    playerId: string;
    arena: unknown;
    /**
     * Which map, by id.
     *
     * The arena travels but the *geometry* does not — every box, torch and named
     * area of a cathedral is a great deal to send and the client already has all
     * of it on disk. So the room says which map it is playing and the client
     * looks it up, which is also what stops a client drawing one level while the
     * server resolves collisions against another.
     */
    mapId: string;
    tickRate: number;
  }
  | { t: 'denied'; why: string }
  /**
   * A room was opened, and this is what it is called.
   *
   * The id is the server's to choose, not the client's — a client that named its
   * own room could take one that already exists and walk straight into somebody
   * else's match, or take one of the persistent ids and quietly replace it.
   */
  | { t: 'opened'; room: string }
  /**
   * The world as this client is entitled to see it, plus what has happened.
   *
   * One message rather than three, because they are one moment: a snapshot with
   * the events that produced it arriving separately would let a client draw a
   * death before the body moved.
   */
  | {
    t: 'snapshot';
    state: unknown;
    events: SimEvent[];
    chat: ChatMessage[];
    /** The last input from *this* client that the room had processed. */
    ack: number;
  }
  | { t: 'lobbies'; rooms: LobbySummary[] }
  | { t: 'pong' };

/**
 * Everything in a `SimState` that cannot or need not travel.
 *
 * `rng` is a live object with a mutable cursor — it cannot be serialised
 * meaningfully and a client has no business rolling anything anyway, since every
 * roll that matters happened on the server. `arena` is sent once at `welcome`
 * and reattached on arrival, because re-sending a cathedral sixty times a second
 * would be most of the bandwidth in the game.
 */
const NOT_ON_THE_WIRE = ['rng', 'arena'] as const;

/** What a snapshot actually contains: a state minus the things above. */
export type Snapshot = Omit<SimState, 'rng' | 'arena'> & {
  /** The parts of an arena that do change, and so must ride along. */
  objectives: {
    pools: { id: string; owner: string; progress: number }[];
    shrines: { id: string; conquered: boolean; power: number }[];
  };
};

export function toSnapshot(state: SimState): Snapshot {
  const bare = { ...state } as Record<string, unknown>;
  for (const key of NOT_ON_THE_WIRE) delete bare[key];

  return {
    ...(bare as Omit<SimState, 'rng' | 'arena'>),
    objectives: {
      pools: state.arena.pools.map((p) => ({
        id: p.id, owner: p.owner, progress: p.progress,
      })),
      shrines: state.arena.shrines.map((s) => ({
        id: s.id, conquered: s.conquered, power: s.power,
      })),
    },
  };
}

/**
 * Puts a snapshot back together against the arena the client already has.
 *
 * The arena object is *reused* rather than rebuilt, which matters for more than
 * speed: the renderer holds references into it — every wall mesh, every torch
 * light — and handing it a new one each frame would mean rebuilding the level
 * sixty times a second while looking at it.
 */
export function fromSnapshot(snapshot: Snapshot, arena: SimState['arena']): SimState {
  for (const p of snapshot.objectives.pools) {
    const pool = arena.pools.find((x) => x.id === p.id);
    if (!pool) continue;
    pool.owner = p.owner as typeof pool.owner;
    pool.progress = p.progress;
  }
  for (const s of snapshot.objectives.shrines) {
    const shrine = arena.shrines.find((x) => x.id === s.id);
    if (!shrine) continue;
    shrine.conquered = s.conquered;
    shrine.power = s.power;
  }

  const { objectives: _ignored, ...rest } = snapshot;
  return {
    ...(rest as Omit<SimState, 'rng' | 'arena'>),
    arena,
    // Never used on a client: every roll that matters happened on the server.
    rng: null as unknown as SimState['rng'],
  } as SimState;
}

/** Parses a message, or returns null. A malformed frame is not a crash. */
export function readMessage<T>(raw: unknown): T | null {
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw) as T;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}
